<template>
  <div
    id="tasks-container"
    v-click-outside="closeDropdown"
    class="tasks-container"
    :class="{
      active: isDropdownOpen,
      blinking: doesBackgroundTasksExists || inProgressTasks.length,
    }">
    <div
      id="tasks-icon"
      v-tooltip="{
        content: $options.filters.translate('ongoing_tasks_tooltip'),
        offset: 6,
      }"
      class="tasks-icon icon-wrapper flex-center pointer no-userselect">
      <i id="handle-dropdown" class="fa-solid fa-list-check" @click="handleDropdown" />
    </div>
    <div v-if="isDropdownOpen" id="tasks-dropdown" class="tasks-dropdown">
      <div>
        <div class="dropdown-header">
          <div class="dropdown-title">
            {{ 'ongoing_tasks' | translate }}
          </div>
          <div class="dropdown-description">
            {{ 'ongoing_tasks_description' | translate }}
          </div>
        </div>
        <div v-if="!tasksFetched" class="loader-center">
          <MdtLoader />
        </div>
        <div v-else class="tasks-inner-container">
          <div v-if="inProgressTasks.length || completedTasks.length" class="tasks-list">
            <div v-for="(task) in inProgressTasks" :key="`in-progress-${task.id}`">
              <BackgroundTaskItem :task="task" />
            </div>
            <div v-for="(task) in completedTasks" :key="`completed-${task.id}`">
              <BackgroundTaskItem :task="task" />
            </div>
          </div>
          <div v-else class="center-it">
            <img src="@/assets/images/nothing-found.png">
            <div class="no-tasks-message">
              {{ 'no_background_tasks_message' | translate }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mixinToggleDropdown } from '@/mixins';
import { BACKGROUND_TASK } from '@/utility/constants';
import BackgroundTaskItem
  from '@/components/navigation-top/NavigationTopBackgroundTasks/BackgroundTaskItem.vue';

const { IN_PROGRESS, COMPLETED } = BACKGROUND_TASK.PROGRESS_STATE;
export default {
  name: 'NavigationTopBackgroundTasks',
  components: {
    BackgroundTaskItem,
  },
  mixins: [mixinToggleDropdown],
  data() {
    return {
      refreshInterval: null,
      inProgressTasks: [],
      completedTasks: [],
      tasksFetched: false,
      activeTasksCounter: 0,
      checkTasksInterval: null,
    };
  },
  computed: {
    isBackgroundTasksOpen() {
      return this.$store.getters['table/getIsBackgroundTasksOpen'];
    },
    doesBackgroundTasksExists() {
      return this.$store.getters['table/getDoesBackgroundTasksExists'];
    },
  },
  watch: {
    isDropdownOpen(val) {
      /*
       * Watcher for `isDropdownOpen`.
       ! @param {boolean} val - Indicates if the dropdown is open (`true`) or closed (`false`).
       */
      if (val) {
        /*
        * - fetches tasks,
        * - clears the background check interval.
        * */
        this.getDataOnDropdownOpen();
        // Clear checkTasksInterval when the dropdown is open
        this.stopInterval('checkTasksInterval');

        // * Start a refresh interval for active tasks.
        this.refreshInterval = setInterval(this.refreshActiveTasksProgress, 10000);
      } else {
        /*
        * - updates the store to close the dropdown,
        * - clears the refresh interval,
        * */
        this.$store.dispatch('table/toggleBackgroundTasksDropdown', this.isDropdownOpen);

        this.stopInterval('refreshInterval');
        this.inProgressTasks = [];
        this.completedTasks = [];

        // * start background tasks pinging.
        this.pingBackgroundTasks();
      }
    },
    isBackgroundTasksOpen(val) {
      // * Listen for changes to `isBackgroundTasksOpen` from the store.
      // * This opens automatically when sendBulkMail actions are called
      if (val) {
        this.isDropdownOpen = true;
      }
    },
  },
  mounted() {
    // * On mount, check if background tasks exist and start pinging.
    this.pingBackgroundTasks();
  },
  beforeDestroy() {
    // * Clear intervals when the component is destroyed to prevent memory leaks.
    this.clearAllIntervals();
  },
  methods: {
    handleDropdown() {
      this.toggleDropdown();
      this.$store.dispatch('table/toggleBackgroundTasksDropdown', this.isDropdownOpen);
    },
    getDataOnDropdownOpen() {
      // * Fetch in-progress and completed tasks when the dropdown is opened.
      Promise.all([
        this.$store.dispatch('table/getBulkMailGroupTasks', { progress_state: IN_PROGRESS }),
        this.$store.dispatch('table/getBulkMailGroupTasks', { progress_state: COMPLETED }),
      ]).then(([inProgressRes, completedRes]) => {
        this.inProgressTasks = inProgressRes.data;
        this.activeTasksCounter = inProgressRes.data.length;
        this.completedTasks = completedRes.data;

        // * If there are no in-progress tasks, clear the refresh interval.
        if (!this.inProgressTasks.length) {
          this.stopInterval('refreshInterval');
        }
      }).catch((error) => {
        console.error('Failed to fetch background tasks:', error);
      }).finally(() => {
        this.tasksFetched = true;
      });
    },
    refreshCompletedTasksProgress() {
      this.$store.dispatch('table/getBulkMailGroupTasks', { progress_state: COMPLETED })
        .then((res) => {
          this.completedTasks = res.data;
          return res;
        })
        .catch((error) => {
          console.error('Failed to refresh completed tasks:', error);
        });
    },
    refreshActiveTasksProgress() {
      this.$store.dispatch('table/getBulkMailGroupTasks', { progress_state: IN_PROGRESS })
        .then((res) => {
          this.inProgressTasks = res.data;

          // * Update the active tasks counter and refresh completed tasks if the count has changed.
          if (res.data.length !== this.activeTasksCounter) {
            this.activeTasksCounter = res.data.length;
            this.refreshCompletedTasksProgress();
          }
          // * If there are no in-progress tasks, clear the refresh interval and update the store.
          if (!this.inProgressTasks.length) {
            this.stopInterval('refreshInterval');
            this.$store.dispatch('table/setDoesBackgroundTasksExists', false);
          }
        })
        .catch((error) => {
          console.error('Failed to refresh active tasks:', error);
        });
    },
    checkBackgroundTasksExistence() {
      this.$store.dispatch('table/checkDoesBackgroundTasksExists', IN_PROGRESS)
        .then((exists) => {
          if (!exists) {
            this.stopInterval('checkTasksInterval');
          }
        })
        .catch((error) => {
          console.error('Failed to check background tasks existence:', error);
        });
    },
    pingBackgroundTasks() {
      /*
       * On mount, check if background tasks exist.
       * If they do, start an interval to check every 10 seconds.
       * If at any point no tasks exist, clear the interval.
       ! This ping stops once the dropdown is opened.
       */
      this.$store.dispatch('table/checkDoesBackgroundTasksExists', IN_PROGRESS)
        .then((initialExists) => {
          if (!initialExists) return;
          this.checkTasksInterval = setInterval(this.checkBackgroundTasksExistence, 10000);
        })
        .catch((error) => {
          console.error('Failed to ping background tasks:', error);
        });
    },
    stopInterval(prop) {
      if (this[prop]) {
        clearInterval(this[prop]);
        this[prop] = null;
      }
    },
    clearAllIntervals() {
      this.stopInterval('checkTasksInterval');
      this.stopInterval('refreshInterval');
    },
  },
};
</script>

<style scoped lang="scss">
  .blinking {
    &:before {
      content: '';
      position: absolute;
      width: 40px;
      height: 40px;
      background-color: $color-success-light;
      border-radius: 50%;
      opacity: 1;
      animation: blink-green-bg 800ms infinite;
    }
  }

  @keyframes blink-green-bg {
    0% {
      width: 24px;
      height: 24px;
      opacity: 0;
    }
    70% {
      width: 40px;
      height: 40px;
      opacity: 1;
    }
    90% {
      opacity: 0.5;
    }
    100% {
      opacity: 0;
      width: 40px;
      height: 40px;
    }
  }
  .tasks-container {
    display: flex;
    align-items: center;
    justify-content: center;

    .tasks-dropdown {
      position: absolute;
      width: 500px;
      height: 600px;
      top: calc(100% + 8px);
      right: -8px;
      padding: 30px;
      background-color: $color-back-primary;
      box-shadow: 0 2px 20px 0 #0000002a;
      font-size: 0.875rem;
      border-radius: 4px;
      overflow: auto;
      scrollbar-width: none; /* Firefox */
      -ms-overflow-style: none; /* IE and Edge */

      &::-webkit-scrollbar {
        display: none; /* Chrome, Safari, and Opera */
      }

      .dropdown-header {
        margin-bottom: 20px;

        .dropdown-title {
          font-size: $font-size-base;
          font-weight: $font-weight-bold;
          color: $color-text-primary;
        }

        .dropdown-description {
          font-size: 14px;
          color: $color-text-secondary;
          margin-top: 4px;
        }
      }

      .loader-center {
        display: flex;
        align-items: center;
        justify-content: center;
        height: calc(100% - 60px); // Adjust height to fit header
      }

      .tasks-list {
        display: flex;
        flex-direction: column;
        gap: 20px;

        &>div:last-child {
          margin-bottom: 20px;
        }
      }

      .tasks-inner-container {
        height: calc(100% - 60px); // Adjust height to fit header

        .no-tasks-message {
          font-size: 14px;
          color: $color-text-secondary;
          text-align: center;
        }
      }
    }
  }
</style>
